// ✅ FIXED: demo-request.service.js

import axios from "axios";
import config from "../environment/config";
import { authHeader } from "./auth.header";

const API_BASE_URL = `${config.API_URL}/api/seo-user`;

/* ---------------------------------------
   Submit Demo Request (Public/Authenticated)
---------------------------------------- */
const submitDemoRequest = async (demoRequestData) => {
  try {
    console.log('📤 Submitting demo request:', demoRequestData);
    
    const response = await axios.post(
      `${API_BASE_URL}/demo-request`,
      demoRequestData,
      {
        headers: {
          ...authHeader(),
          "Content-Type": "application/json",
        },
        timeout: 10000
      }
    );
    
    console.log('✅ Demo request submitted:', response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Error submitting demo request:", error.response?.data || error);
    throw error;
  }
};

/* ---------------------------------------
   Transform Form Data to Backend Format
   ✅ FIXED: Added planName parameter
---------------------------------------- */
const transformDemoRequestData = (formData, productName = null, planName = null) => {
  const transformedData = {
    name: formData.firstName || formData.name,
    type: formData.userType || formData.type,
    phone: parseInt(formData.mobileNumber || formData.phone, 10),
    email: formData.email,
    productName: productName || null,  // ✅ Send as separate field
    planName: planName || null,
    message: formData.message || "",   // ✅ Keep message separate
  };
  
  console.log('🔄 Transformed form data:', transformedData);
  return transformedData;
};

/* ---------------------------------------
   ✅ Get Current User's Demo Requests
   Uses /my-requests endpoint with JWT authentication
---------------------------------------- */
const getMyDemoRequests = async () => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      console.warn('⚠️ No token found - user not authenticated');
      return [];
    }

    console.log('📡 Fetching my demo requests...');

    const response = await axios.get(
      `${API_BASE_URL}/demo-request/my-requests`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        timeout: 15000
      }
    );

    console.log('✅ My demo requests received:', response.data);
    return Array.isArray(response.data) ? response.data : [];
    
  } catch (error) {
    console.error("❌ Error fetching my demo requests:", error.response?.data || error);
    
    // Re-throw authentication errors
    if (error.response?.status === 401 || error.response?.status === 403) {
      throw new Error('Authentication required. Please login again.');
    }
    
    throw error;
  }
};

/* ---------------------------------------
   Get All Demo Requests (Admin)
---------------------------------------- */
const getAllDemoRequests = async () => {
  try {
    console.log('📡 Fetching all demo requests (admin)...');
    
    const response = await axios.get(
      `${API_BASE_URL}/demo-request`,
      {
        headers: authHeader(),
        timeout: 15000
      }
    );

    console.log('✅ All demo requests received:', response.data);
    return Array.isArray(response.data) ? response.data : [];
    
  } catch (error) {
    console.error("❌ Error fetching all demo requests:", error.response?.data || error);
    throw error;
  }
};

/* ---------------------------------------
   Get Demo Request by ID
---------------------------------------- */
const getDemoRequestById = async (id) => {
  try {
    console.log('📡 Fetching demo request by ID:', id);
    
    const response = await axios.get(
      `${API_BASE_URL}/demo-request/${id}`,
      {
        headers: authHeader(),
        timeout: 10000
      }
    );

    console.log('✅ Demo request received:', response.data);
    return response.data;
    
  } catch (error) {
    console.error(`❌ Error fetching demo request ${id}:`, error.response?.data || error);
    throw error;
  }
};

/* ---------------------------------------
   Get Demo Requests by Type
---------------------------------------- */
const getDemoRequestsByType = async (type) => {
  try {
    console.log('📡 Fetching demo requests by type:', type);
    
    const response = await axios.get(
      `${API_BASE_URL}/demo-request/type/${type}`,
      {
        headers: authHeader(),
        timeout: 10000
      }
    );

    console.log('✅ Demo requests by type received:', response.data);
    return Array.isArray(response.data) ? response.data : [];
    
  } catch (error) {
    console.error(`❌ Error fetching demo requests by type ${type}:`, error.response?.data || error);
    throw error;
  }
};

/* ---------------------------------------
   Search Demo Requests by Name
---------------------------------------- */
const searchDemoRequestsByName = async (name) => {
  try {
    console.log('📡 Searching demo requests by name:', name);
    
    const response = await axios.get(
      `${API_BASE_URL}/demo-request/search`,
      {
        params: { name },
        headers: authHeader(),
        timeout: 10000
      }
    );

    console.log('✅ Search results received:', response.data);
    return Array.isArray(response.data) ? response.data : [];
    
  } catch (error) {
    console.error(`❌ Error searching demo requests by name ${name}:`, error.response?.data || error);
    throw error;
  }
};

/* ---------------------------------------
   Update Demo Request (Admin)
---------------------------------------- */
const updateDemoRequest = async (id, updateData) => {
  try {
    console.log('🔄 Updating demo request:', id, updateData);

    const response = await axios.put(
      `${API_BASE_URL}/demo-request/${id}`,
      updateData,
      {
        headers: {
          ...authHeader(),
          "Content-Type": "application/json",
        },
        timeout: 10000,
      }
    );

    console.log('✅ Demo request updated:', response.data);
    return response.data;
    
  } catch (error) {
    console.error(`❌ Error updating demo request ${id}:`, error.response?.data || error);
    throw error;
  }
};

/* ---------------------------------------
   ✅ Default Export
---------------------------------------- */
const demoRequestService = {
  submitDemoRequest,
  transformDemoRequestData,
  getMyDemoRequests,
  getAllDemoRequests,
  getDemoRequestById,
  getDemoRequestsByType,
  searchDemoRequestsByName,
  updateDemoRequest,
};

// Named exports for backward compatibility
export { 
  submitDemoRequest, 
  transformDemoRequestData, 
  getMyDemoRequests,
  getAllDemoRequests,
  getDemoRequestById,
  getDemoRequestsByType,
  searchDemoRequestsByName,
  updateDemoRequest 
};

export default demoRequestService;